import React from 'react'
import { 
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CalendarIcon,
  InformationCircleIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'

const DetailsOfComplaintPage: React.FC = () => {
  // Current month data
  const currentMonthData = {
    month: 'September 2025',
    data: [
      { source: 'Directly from Investors', received: 0, resolved: 0, pending: 0, avgResolutionTime: '-' },
      { source: 'SEBI (SCORES)', received: 0, resolved: 0, pending: 0, avgResolutionTime: '-' },
      { source: 'Other Sources', received: 0, resolved: 0, pending: 0, avgResolutionTime: '-' }
    ]
  }

  // Monthly trend data (last 12 months)
  const monthlyTrendData = [
    { month: 'October 2024', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'November 2024', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'December 2024', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'January 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'February 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'March 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'April 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'May 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'June 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'July 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'August 2025', received: 0, resolved: 0, pending: 0, carried: 0 },
    { month: 'September 2025', received: 0, resolved: 0, pending: 0, carried: 0 }
  ]

  // Annual trend data
  const annualTrendData = [
    { year: '2023-24', received: 0, resolved: 0, pending: 0, carried: 0 },
    { year: '2024-25', received: 0, resolved: 0, pending: 0, carried: 0 },
    { year: '2025-26', received: 0, resolved: 0, pending: 0, carried: 0 }
  ]

  const totalReceived = currentMonthData.data.reduce((sum, item) => sum + item.received, 0)
  const totalResolved = currentMonthData.data.reduce((sum, item) => sum + item.resolved, 0)
  const totalPending = currentMonthData.data.reduce((sum, item) => sum + item.pending, 0)

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ClipboardDocumentListIcon aria-hidden="true" focusable="false" className="w-16 h-16 mx-auto mb-6 text-purple-200" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Details of Complaints
            </h1>
            <p className="text-2xl md:text-3xl font-bold mb-6 text-purple-300">
              AnalyzerView Market Research
            </p>
            <div className="bg-purple-700/30 rounded-lg p-6 max-w-4xl mx-auto">
              <p className="text-lg text-purple-100 mb-2">
                SEBI Registered Research Analyst Registration No.: INH000023719
              </p>
              <p className="text-base text-purple-200">
                Transparency in complaint resolution and customer service excellence
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Month Data Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-800 border-gray-700">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-900/30 rounded-full flex items-center justify-center mr-4">
                <CalendarIcon aria-hidden="true" focusable="false" className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Data for the Month Ending {currentMonthData.month}
                </h2>
                <p className="text-gray-300">Current month complaint statistics</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <caption className="sr-only">
                  Complaint statistics for the month ending {currentMonthData.month}, by source of complaint
                </caption>
                <thead className="bg-purple-900/20">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Sr. No.
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Source of Complaint
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Received
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Resolved*
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Total Pending#
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-300 uppercase tracking-wider">
                      Avg Resolution Time^
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-600">
                  {currentMonthData.data.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                        {index + 1}
                      </td>
                      <th scope="row" className="px-6 py-4 whitespace-nowrap text-sm font-normal text-left text-gray-200">
                        {item.source}
                      </th>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-center">
                        {item.received}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-center">
                        {item.resolved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-center">
                        {item.pending}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-center">
                        {item.avgResolutionTime}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-purple-900/20 font-semibold">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-200"></td>
                    <th scope="row" className="px-6 py-4 whitespace-nowrap text-sm text-left font-bold text-purple-300">
                      Grand Total
                    </th>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-300 text-center">
                      {totalReceived}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-300 text-center">
                      {totalResolved}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-300 text-center">
                      {totalPending}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-300 text-center">
                      -
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-xs text-gray-400 space-y-1">
              <p>*Inclusive of complaints of previous years resolved in the current month.</p>
              <p>#Inclusive of complaints pending as on the last day of the month.</p>
              <p>^Average Resolution time is the sum total of time taken to resolve each complaint, in days, in the current month divided by total number of complaints resolved in the current month.</p>
            </div>
          </Card>
        </div>
      </section>

      {/* Monthly Trend Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-900 border-gray-700">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-900/30 rounded-full flex items-center justify-center mr-4">
                <ChartBarIcon aria-hidden="true" focusable="false" className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Trend of Monthly Disposal of Complaints
                </h2>
                <p className="text-gray-300">Last 12 months complaint resolution trends</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <caption className="sr-only">
                  Monthly disposal of complaints over the last 12 months
                </caption>
                <thead className="bg-purple-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Sr. No.
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Month
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Received
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Resolved*
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Total Pending#
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Carried Forward
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyTrendData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <th scope="row" className="px-6 py-4 whitespace-nowrap text-sm font-normal text-left text-gray-900">
                        {item.month}
                      </th>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.received}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.resolved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.pending}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.carried}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-xs text-gray-300 space-y-1">
              <p>*Inclusive of complaints of previous years resolved in the current month.</p>
              <p>#Inclusive of complaints pending as on the last day of the month.</p>
            </div>
          </Card>
        </div>
      </section>

      {/* Annual Trend Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gray-900 border-gray-700">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-900/30 rounded-full flex items-center justify-center mr-4">
                <ChartBarIcon aria-hidden="true" focusable="false" className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Trend of Annual Disposal of Complaints
                </h2>
                <p className="text-gray-300">Yearly complaint resolution summary</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <caption className="sr-only">
                  Annual disposal of complaints by financial year
                </caption>
                <thead className="bg-purple-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Sr. No.
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Year
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Received
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Resolved*
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Total Pending#
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-purple-900 uppercase tracking-wider">
                      Carried Forward
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {annualTrendData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <th scope="row" className="px-6 py-4 whitespace-nowrap text-sm font-normal text-left text-gray-900">
                        {item.year}
                      </th>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.received}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.resolved}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.pending}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {item.carried}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-xs text-gray-300 space-y-1">
              <p>*Inclusive of complaints of previous years resolved in the current year.</p>
              <p>#Inclusive of complaints pending as on the last day of the year.</p>
            </div>
          </Card>
        </div>
      </section>

      {/* Grievance Process Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="bg-gray-900 border-gray-700">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <InformationCircleIcon aria-hidden="true" focusable="false" className="w-8 h-8 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">How to File a Complaint</h2>
              </div>
              <ol className="space-y-4 list-none">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span aria-hidden="true" className="text-xs font-bold text-purple-300">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Direct Contact</p>
                    <p className="text-gray-300">Contact us directly through email or phone for immediate assistance.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span aria-hidden="true" className="text-xs font-bold text-purple-300">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">SEBI SCORES</p>
                    <p className="text-gray-300">If not satisfied, escalate to SEBI's online complaint resolution system.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span aria-hidden="true" className="text-xs font-bold text-purple-300">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Online Dispute Resolution</p>
                    <p className="text-gray-300">Use Smart ODR portal for alternative dispute resolution.</p>
                  </div>
                </li>
              </ol>
            </Card>

            <Card className="bg-gray-900 border-gray-700">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PhoneIcon aria-hidden="true" focusable="false" className="w-8 h-8 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Contact Information</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <EnvelopeIcon aria-hidden="true" focusable="false" className="w-5 h-5 text-purple-400" />
                    <span className="font-semibold text-white">Email</span>
                  </div>
                  <p className="text-purple-300 font-medium">rahul@analyzerview.com</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <PhoneIcon aria-hidden="true" focusable="false" className="w-5 h-5 text-purple-400" />
                    <span className="font-semibold text-white">Phone</span>
                  </div>
                  <p className="text-purple-300 font-medium">+91 93820 93168</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <span aria-hidden="true" className="text-purple-400 text-lg">🏢</span>
                    <span className="font-semibold text-white">Address</span>
                  </div>
                  <p className="text-purple-300">
                    Kukshimla, Raidogachia, Purbasthali,<br />
                    Purba Bardhaman, West Bengal – 713513
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* External Links Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            External Complaint Resolution Portals
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="text-center bg-gray-800 border-gray-700">
              <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span aria-hidden="true" className="text-2xl">🏛️</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">SEBI SCORES</h3>
              <p className="text-gray-300 mb-4">
                Securities and Exchange Board of India's online complaint resolution system
              </p>
              <a
                href="https://scores.sebi.gov.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
              >
                Visit SCORES Portal<span className="sr-only"> (opens in a new tab)</span>
              </a>
            </Card>

            <Card className="text-center bg-gray-800 border-gray-700">
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span aria-hidden="true" className="text-2xl">⚖️</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Smart ODR</h3>
              <p className="text-gray-300 mb-4">
                Online Dispute Resolution platform for alternative resolution
              </p>
              <a
                href="https://smartodr.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
              >
                Visit Smart ODR<span className="sr-only"> (opens in a new tab)</span>
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-300 mb-4">
            <strong>Note:</strong> We are committed to resolving all complaints in a timely and fair manner. 
            Our goal is to maintain zero pending complaints and provide exceptional customer service.
          </p>
          <p className="text-xs text-gray-400">
            This data is updated monthly and reflects our commitment to transparency and regulatory compliance.
          </p>
        </div>
      </section>
    </div>
  )
}

export default DetailsOfComplaintPage